import React, { CSSProperties, FC, useEffect, useRef, useState } from 'react';
import { styled } from 'linaria/react';
import { useBreakpoint } from '@shared/lib/react';
type Leaf = {
  top: number;
  left: number;
  color: string;
  size: number;
  rotation: number;
  sway: number;
  swayDuration: number;
  delay: number;
  flip: boolean;
};
type LeafLayer = {
  key: string;
  count: {
    mobile: number;
    desktop: number;
  };
  /** Размер листа в px — [min, max]. */
  size: [number, number];
  /** Амплитуда покачивания в px — [min, max]. */
  sway: [number, number];
};

/** Слои листьев: крупные падают быстрее и качаются шире, мелкие — медленно и
 *  размыто. Разница в скорости и резкости даёт параллакс, как раньше у звёзд. */
const LEAF_LAYERS: LeafLayer[] = [{
  key: 'big',
  count: {
    mobile: 2,
    desktop: 7
  },
  size: [28, 40],
  sway: [28, 48]
}, {
  key: 'avg',
  count: {
    mobile: 3,
    desktop: 12
  },
  size: [16, 24],
  sway: [18, 32]
}, {
  key: 'small',
  count: {
    mobile: 4,
    desktop: 18
  },
  size: [8, 14],
  sway: [10, 20]
}];

/** Золотая пыль — мелкие блики в подсветке. Отдельный слой, не листья. */
const DUST_COUNT = {
  mobile: 16,
  desktop: 70
};

/** Один оранжевый на все листья — пёстрая палитра шумела на фоне. */
const LEAF_COLOR = '#f2861f';
const RESIZE_DEBOUNCE_MS = 300;
const randomInt = (max: number) => Math.floor(Math.random() * max);
const randomBetween = (min: number, max: number) => min + Math.random() * (max - min);
const createLeaves = (layer: LeafLayer, count: number, width: number, height: number): Leaf[] => Array.from({
  length: count
}, () => ({
  top: randomInt(height),
  left: randomInt(width),
  color: LEAF_COLOR,
  size: randomBetween(layer.size[0], layer.size[1]),
  rotation: randomBetween(-180, 180),
  sway: randomBetween(layer.sway[0], layer.sway[1]),
  swayDuration: randomBetween(3.5, 6.5),
  delay: -randomBetween(0, 6),
  flip: Math.random() < 0.5
}));
const createDust = (count: number, width: number, height: number) => Array.from({
  length: count
}, () => ({
  top: randomInt(height),
  left: randomInt(width)
}));
const LeafIcon: FC = () => <svg viewBox="0 0 32 32" aria-hidden="true">
    <path d="M16 2l2.5 5.5 3.5-2-1 5.5 6-1.5-2.5 5 5.5 2.5-5 2.5 2 4.5-6-1.5-3.5 4-.5-2.5V31h-2v-7l-.5 2.5-3.5-4-6 1.5 2-4.5-5-2.5 5.5-2.5-2.5-5 6 1.5-1-5.5 3.5 2z" />
  </svg>;
const AutumnAnimatedBackground: FC = () => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const {
    isMobile
  } = useBreakpoint();
  const [size, setSize] = useState({
    width: 0,
    height: 0
  });
  const [layers, setLayers] = useState<Leaf[][]>([]);
  const [dust, setDust] = useState<{
    top: number;
    left: number;
  }[]>([]);
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const measure = () => setSize({
      width: wrapper.offsetWidth,
      height: wrapper.offsetHeight
    });
    measure();
    let timeout: number | undefined;
    const observer = new ResizeObserver(() => {
      if (timeout) clearTimeout(timeout);
      timeout = window.setTimeout(measure, RESIZE_DEBOUNCE_MS);
    });
    observer.observe(wrapper);
    return () => {
      observer.disconnect();
      if (timeout) clearTimeout(timeout);
    };
  }, []);
  useEffect(() => {
    if (size.width === 0 || size.height === 0) return;
    setLayers(LEAF_LAYERS.map(layer => createLeaves(layer, isMobile ? layer.count.mobile : layer.count.desktop, size.width, size.height)));
    setDust(createDust(isMobile ? DUST_COUNT.mobile : DUST_COUNT.desktop, size.width, size.height));
  }, [size.width, size.height, isMobile]);

  // Две одинаковые копии слоя (на -100% и на 0) едут вниз на свою высоту —
  // стык копий совпадает, петля не видна.
  const copies = ['-100%', '0'] as const;
  return <Wrapper ref={wrapperRef}>
      {dust.length > 0 && copies.map(offset => <FallGroup key={`dust-${offset}`} data-layer="dust" style={{
      top: offset
    }}>
            {dust.map((mote, index) => <Dust key={index} style={{
        top: mote.top,
        left: mote.left
      }} />)}
          </FallGroup>)}

      {LEAF_LAYERS.map((layer, layerIndex) => {
      const leaves = layers[layerIndex];
      if (!leaves) return null;
      return copies.map(offset => <FallGroup key={`${layer.key}-${offset}`} data-layer={layer.key} style={{
        top: offset
      }}>
            {leaves.map((leaf, leafIndex) => <FallingLeaf key={leafIndex} data-layer={layer.key} style={{
          top: leaf.top,
          left: leaf.left,
          width: leaf.size,
          height: leaf.size,
          color: leaf.color,
          animationDuration: `${leaf.swayDuration}s`,
          animationDelay: `${leaf.delay}s`,
          '--leaf-rot': `${leaf.rotation}deg`,
          '--leaf-sway': `${leaf.sway}px`,
          '--leaf-flip': leaf.flip ? -1 : 1
        } as CSSProperties}>
                <LeafIcon />
              </FallingLeaf>)}
          </FallGroup>);
    })}
    </Wrapper>;
};
export default AutumnAnimatedBackground;
const Wrapper = /*#__PURE__*/styled('div')({
  name: "Wrapper",
  class: "w1ex6xmn",
  propsAsIs: false
});
const FallGroup = /*#__PURE__*/styled('div')({
  name: "FallGroup",
  class: "f231u4y",
  propsAsIs: false
});
const Dust = /*#__PURE__*/styled('div')({
  name: "Dust",
  class: "d9t63cr",
  propsAsIs: false
});
const FallingLeaf = /*#__PURE__*/styled('div')({
  name: "FallingLeaf",
  class: "f85s2qo",
  propsAsIs: false
});

require("./AutumnAnimatedBackground.linaria.module.css!=!../../../../node_modules/linaria/node_modules/@linaria/webpack4-loader/lib/outputCssLoader.js?cacheProvider=!./AutumnAnimatedBackground.tsx");